import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';
import { Loader2, MapPin, Navigation, Clock, DollarSign } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Event type colors for markers
const EVENT_TYPE_COLORS = {
  flight: '#3B82F6',     // blue
  hotel: '#A855F7',      // purple
  transport: '#22C55E',  // green
  meal: '#F97316',       // orange
  meeting: '#06B6D4',    // cyan
  shoot: '#EC4899',      // pink
  activity: '#EAB308',   // yellow
  conference: '#6366F1', // indigo
  production: '#F43F5E', // rose
  other: '#9CA3AF',      // gray
};

// Event type labels
const EVENT_TYPE_LABELS = {
  flight: 'Flight',
  hotel: 'Hotel', 
  transport: 'Transport',
  meal: 'Meal',
  meeting: 'Meeting',
  shoot: 'Shoot',
  activity: 'Activity',
  conference: 'Conference',
  production: 'Production',
  other: 'Other',
};

// Create custom marker icon SVG
const createMarkerIcon = (color, isSelected = false) => {
  return {
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    fillColor: color,
    fillOpacity: 1,
    strokeColor: isSelected ? '#ffffff' : '#000000',
    strokeWeight: isSelected ? 3 : 1.5,
    scale: isSelected ? 2 : 1.5,
    anchor: { x: 12, y: 22 },
    labelOrigin: { x: 12, y: 9 },
  };
};

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: 30.2672,  // Austin, TX default
  lng: -97.7431,
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [
    // Dark mode map styling
    { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8b8b8b' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#333366' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#6b6b8d' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#16162a' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#1f1f3d' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#7b7b9d' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2f1a' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#4a8b4a' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a4a' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a3a6a' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f3d' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1f1f3d' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a6b8b' }] },
  ],
};

const ItineraryMap = ({ events = [], itineraryLocation = '' }) => {
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [geocodingComplete, setGeocodingComplete] = useState(false);
  const [map, setMap] = useState(null);
  const [activeRouteIndex, setActiveRouteIndex] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  // Sort events chronologically
  const sortedEvents = useMemo(() => {
    return [...events]
      .filter(e => e.location) // Only events with locations
      .sort((a, b) => {
        const dateA = `${a.date} ${a.start_time || '00:00'}`;
        const dateB = `${b.date} ${b.start_time || '00:00'}`;
        return dateA.localeCompare(dateB);
      });
  }, [events]);

  // Geocode locations to coordinates
  const geocodeLocations = useCallback(async () => {
    if (!isLoaded || !window.google || sortedEvents.length === 0) return;

    const geocoder = new window.google.maps.Geocoder();
    const newMarkers = [];

    for (let i = 0; i < sortedEvents.length; i++) {
      const event = sortedEvents[i];
      if (!event.location) continue;

      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: event.location }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              reject(new Error(`Geocoding failed for ${event.location}`));
            }
          });
        });

        newMarkers.push({
          id: event.id,
          position: {
            lat: result.lat(),
            lng: result.lng(),
          },
          event: event,
          label: String(i + 1),
        });
      } catch (error) {
        console.warn('Geocoding error:', error.message);
      }
    }

    setMarkers(newMarkers);
    setGeocodingComplete(true);

    // Center map on markers
    if (newMarkers.length > 0 && map) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach(m => bounds.extend(m.position));
      map.fitBounds(bounds);
      
      // If only one marker, zoom in more
      if (newMarkers.length === 1) {
        map.setZoom(14);
      }
    }
  }, [isLoaded, sortedEvents, map]);

  useEffect(() => {
    if (isLoaded && sortedEvents.length > 0) {
      geocodeLocations();
    }
  }, [isLoaded, sortedEvents, geocodeLocations]);

  // Create polyline path for route
  const routePath = useMemo(() => {
    return markers.map(m => m.position);
  }, [markers]);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Handle clicking on route order number
  const handleRouteItemClick = (marker, index) => {
    setSelectedMarker(marker);
    setActiveRouteIndex(index);
    
    // Pan to the marker location
    if (map && marker.position) {
      map.panTo(marker.position);
      map.setZoom(15);
    }
  };

  // Handle marker click
  const handleMarkerClick = (marker, index) => {
    setSelectedMarker(marker);
    setActiveRouteIndex(index);
  };

  // Clear selection
  const handleCloseInfoWindow = () => {
    setSelectedMarker(null);
    setActiveRouteIndex(null);
  };

  if (loadError) {
    return (
      <div className="bg-black border border-white/30 rounded-2xl p-6 text-center">
        <MapPin size={32} className="mx-auto text-red-400 mb-2" />
        <p className="text-red-400">Failed to load Google Maps</p>
        <p className="text-xs text-gray-500 mt-1">Please check your API key configuration</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-black border border-white/30 rounded-2xl p-6 flex items-center justify-center h-[400px]">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto animate-spin text-white/50 mb-2" />
          <p className="text-gray-400 text-sm">Loading map...</p>
        </div>
      </div>
    );
  }

  if (sortedEvents.length === 0) {
    return (
      <div className="bg-black border border-white/30 rounded-2xl p-6 text-center h-[300px] flex flex-col items-center justify-center">
        <MapPin size={32} className="mx-auto text-gray-600 mb-2" />
        <p className="text-gray-400">No event locations to display</p>
        <p className="text-xs text-gray-500 mt-1">Add events with locations to see them on the map</p>
      </div>
    );
  }

  return (
    <div className="bg-black border border-white/30 rounded-2xl p-5 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Navigation size={18} /> Route Map
        {markers.length > 0 && (
          <span className="text-xs text-gray-500 font-normal ml-2">
            {markers.length} stop{markers.length !== 1 ? 's' : ''}
          </span>
        )}
      </h2>

      {/* Route Order - Clickable stops */}
      {markers.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {markers.map((marker, index) => {
              const color = EVENT_TYPE_COLORS[marker.event.event_type] || EVENT_TYPE_COLORS.other;
              const isActive = activeRouteIndex === index;
              const typeLabel = marker.event.event_type === 'other' && marker.event.custom_type 
                ? marker.event.custom_type 
                : EVENT_TYPE_LABELS[marker.event.event_type] || 'Event';
              
              return (
                <button
                  key={marker.id}
                  onClick={() => handleRouteItemClick(marker, index)}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer ${
                    isActive 
                      ? 'border-white bg-white/10 shadow-[0_0_12px_rgba(255,255,255,0.2)]' 
                      : 'border-white/10 hover:border-white/30 bg-[#0C0E13]'
                  }`}
                  data-testid={`route-stop-${index + 1}`}
                >
                  {/* Number badge */}
                  <span 
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white transition-transform ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}
                    style={{ backgroundColor: color }}
                  >
                    {index + 1}
                  </span>
                  
                  {/* Event info */}
                  <div className="text-left min-w-0">
                    <p className={`text-xs font-medium truncate max-w-[120px] ${isActive ? 'text-white' : 'text-gray-300'}`}>
                      {marker.event.title}
                    </p>
                    <p className="text-[10px] text-gray-500 truncate max-w-[120px]">
                      {typeLabel}
                    </p>
                  </div>
                  
                  {/* Arrow to next */}
                  {index < markers.length - 1 && (
                    <span className="text-gray-600 text-xs ml-1">→</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={12}
        options={mapOptions}
        onLoad={onMapLoad}
      >
        {/* Route polyline */}
        {routePath.length > 1 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: '#ffffff',
              strokeOpacity: 0.6,
              strokeWeight: 2,
              geodesic: true,
              icons: [{
                icon: {
                  path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 0,
                  scale: 2.5,
                  strokeColor: '#ffffff',
                  fillColor: '#ffffff',
                  fillOpacity: 0.8,
                },
                offset: '50%',
                repeat: '120px',
              }],
            }}
          />
        )}

        {/* Event markers */}
        {markers.map((marker, index) => {
          const isActive = activeRouteIndex === index;
          return (
            <Marker
              key={marker.id}
              position={marker.position}
              label={{
                text: String(index + 1),
                color: '#ffffff',
                fontSize: isActive ? '12px' : '11px',
                fontWeight: 'bold',
              }}
              icon={createMarkerIcon(
                EVENT_TYPE_COLORS[marker.event.event_type] || EVENT_TYPE_COLORS.other,
                isActive
              )}
              onClick={() => handleMarkerClick(marker, index)}
              zIndex={isActive ? 1000 : index}
            />
          );
        })}

        {/* Info window for selected marker */}
        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={handleCloseInfoWindow}
            options={{ maxWidth: 280 }}
          >
            <div className="p-1 min-w-[200px]">
              {/* Header with number and type */}
              <div className="flex items-center gap-2 mb-2">
                <span 
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: EVENT_TYPE_COLORS[selectedMarker.event.event_type] || EVENT_TYPE_COLORS.other }}
                >
                  {activeRouteIndex !== null ? activeRouteIndex + 1 : '?'}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                  {selectedMarker.event.event_type === 'other' && selectedMarker.event.custom_type 
                    ? selectedMarker.event.custom_type 
                    : EVENT_TYPE_LABELS[selectedMarker.event.event_type] || 'Event'}
                </span>
              </div>
              
              {/* Title */}
              <p className="font-semibold text-sm text-gray-900 mb-1">{selectedMarker.event.title}</p>
              
              {/* Date & Time */}
              <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                <Clock size={10} />
                <span>{formatDate(selectedMarker.event.date)}</span>
                {selectedMarker.event.start_time && (
                  <>
                    <span className="mx-1">•</span>
                    <span>
                      {formatTime(selectedMarker.event.start_time)}
                      {selectedMarker.event.end_time && ` - ${formatTime(selectedMarker.event.end_time)}`}
                    </span>
                  </>
                )}
              </div>
              
              {/* Location */}
              <div className="flex items-start gap-1 text-xs text-gray-500 mb-1">
                <MapPin size={10} className="mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">{selectedMarker.event.location}</span>
              </div>
              
              {/* Cost */}
              {selectedMarker.event.cost > 0 && (
                <div className="flex items-center gap-1 text-xs font-medium text-green-600 mt-2 pt-2 border-t border-gray-100">
                  <DollarSign size={10} />
                  <span>${parseFloat(selectedMarker.event.cost).toFixed(2)}</span>
                </div>
              )}
              
              {/* Description */}
              {selectedMarker.event.description && (
                <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100 line-clamp-2">
                  {selectedMarker.event.description}
                </p>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legend - simplified */}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10 text-xs">
        <span className="text-gray-500">Legend:</span>
        {Object.entries(EVENT_TYPE_COLORS)
          .filter(([type]) => markers.some(m => m.event.event_type === type))
          .map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div 
                className="w-2.5 h-2.5 rounded-full" 
                style={{ backgroundColor: color }}
              />
              <span className="text-gray-400 capitalize">{EVENT_TYPE_LABELS[type] || type}</span>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ItineraryMap;
